import { ModuleType, ProxyAuthType, type CreateModuleRequestBody, type Module } from "@eisox/api/core-api";
import { z } from "@eisox/zod";

import { m } from "~/paraglide/messages";

/** Module types creatable from the modules settings page: a boiler room (Eclypse or Niagara) or a relay. */
export const ADD_MODULE_TYPES = [
  ModuleType.ECLYPSE_BOILER_ROOM,
  ModuleType.NIAGARA_BOILER_ROOM,
  ModuleType.ECLYPSE_RELAIS,
] as const;

/** The box runs a single relay routine, so a gateway holds at most one relay module. */
export const gatewayHasRelay = (modules: Module[], gatewayId: string): boolean =>
  modules.some(md => md.gatewayId === gatewayId && md.type === ModuleType.ECLYPSE_RELAIS);

/** Modules speak HTTPS; 443 unless the PLC is exposed on another port. */
const MODULE_DEFAULT_PORT = 443;

/**
 * Add-module form model. Address is `url` (IP) xor `bacnetDeviceId` (BACnet is Eclypse-only). PLC
 * `userName`/`password` are required for Eclypse only. `proxyUserName`/`proxyPassword` are the GTB
 * operator credentials; `isProxyExternal` opens the GTB page in a new tab. All credentials are write-only.
 */
export interface AddModuleForm {
  type: ModuleType;
  gatewayId: string;
  url: string;
  bacnetDeviceId?: number;
  port?: number;
  userName: string;
  password: string;
  gtbEnabled: boolean;
  name: string;
  proxyUserName: string;
  proxyPassword: string;
  isProxyExternal: boolean;
}

export const ADD_MODULE_DEFAULT_VALUES: AddModuleForm = {
  type: ModuleType.ECLYPSE_BOILER_ROOM,
  gatewayId: "",
  url: "",
  bacnetDeviceId: undefined,
  port: MODULE_DEFAULT_PORT,
  userName: "",
  password: "",
  gtbEnabled: false,
  name: "",
  proxyUserName: "",
  proxyPassword: "",
  isProxyExternal: false,
};

export const isNiagara = (type: ModuleType): boolean => type === ModuleType.NIAGARA_BOILER_ROOM;

export const addModuleSchema = z
  .object({
    type: z.enum(ModuleType),
    gatewayId: z.string().nonempty(m.module_settings_required()),
    url: z.string(),
    bacnetDeviceId: z.number().optional(),
    port: z.number().int().min(1).max(65_535),
    userName: z.string(),
    password: z.string(),
    gtbEnabled: z.boolean(),
    name: z.string(),
    proxyUserName: z.string(),
    proxyPassword: z.string(),
    isProxyExternal: z.boolean(),
  })
  .superRefine((data, ctx) => {
    // PLC credentials are mandatory for the eclypse types, not for Niagara.
    if (!isNiagara(data.type)) {
      if (!data.userName) ctx.addIssue({ path: ["userName"], code: "custom", message: m.module_settings_required() });
      if (!data.password) ctx.addIssue({ path: ["password"], code: "custom", message: m.module_settings_required() });
    }
    if (data.gtbEnabled) {
      if (!data.proxyUserName)
        ctx.addIssue({ path: ["proxyUserName"], code: "custom", message: m.module_settings_required() });
      if (!data.proxyPassword)
        ctx.addIssue({ path: ["proxyPassword"], code: "custom", message: m.module_settings_required() });
    }
    // Exactly one address: an IP or a BACnet device id, never both nor neither.
    const hasUrl = !!data.url.trim();
    const hasBacnet = data.bacnetDeviceId !== undefined;
    if (hasUrl === hasBacnet)
      ctx.addIssue({ path: ["url"], code: "custom", message: m.module_add_url_bacnet_exclusive() });
  });

/**
 * Build the create payload. Sends whichever address is set (IP xor BACnet), PLC creds for Eclypse only,
 * and — when GTB is on — a `proxy` whose auth scheme follows the module type (Niagara uses a `Basic`
 * header, Eclypse an operator session cookie). `isExternal` opens the GTB page in a new tab.
 */
export const formToCreateBody = (data: AddModuleForm): CreateModuleRequestBody => ({
  type: data.type,
  gatewayId: data.gatewayId,
  port: data.port ?? MODULE_DEFAULT_PORT,
  isSsl: true,
  ...(data.url.trim() ? { url: data.url } : {}),
  ...(data.bacnetDeviceId !== undefined ? { bacnetDeviceId: data.bacnetDeviceId } : {}),
  ...(isNiagara(data.type) ? {} : { userName: data.userName, password: data.password }),
  ...(data.gtbEnabled && {
    proxy: {
      type: isNiagara(data.type) ? ProxyAuthType.BASIC_AUTH : ProxyAuthType.COOKIE,
      userName: data.proxyUserName,
      password: data.proxyPassword,
      name: data.name || undefined,
      ...(isNiagara(data.type) ? { isExternal: data.isProxyExternal } : {}),
    },
  }),
});
